import { ref } from 'vue'
import { isGameTabLeader } from '~/composables/useMultiTabGuard'
import { useApi } from '~/composables/useApi'
import { useAuthStore } from '~/stores/useAuthStore'
import { usePlayerStore } from '~/stores/usePlayerStore'
import { useGameStore } from '~/stores/useGameStore'

/**
 * Sync farm périodique : checkpoint + crédit or optionnel en une seule requête POST /farm-sync.
 */
export const FARM_SYNC_INTERVAL_MS = 45_000

/** Premier sync après le début du farm (plus tôt que l’intervalle régulier). */
const FARM_SYNC_FIRST_FLUSH_MS = 4_000

const EXPRESS_GAIN_GOLD = 500_000
const DEBOUNCE_EXPRESS_MS = 400
const COMBAT_GOLD_BUFFER_KEY = 'poke_combat_gold_pending'

/** Seuils buffer local — sync plus agressive au-delà (alignés HUD). */
export const FARM_SYNC_BUFFER_MODERATE = 1_000_000_000
export const FARM_SYNC_BUFFER_HIGH = 10_000_000_000
export const FARM_SYNC_BUFFER_CRITICAL = 50_000_000_000

export type FarmSyncProfile = {
  minGapMs: number
  maxPasses: number
  intervalMs: number
}

export function resolveFarmSyncProfile(buffer: number): FarmSyncProfile {
  if (buffer >= FARM_SYNC_BUFFER_CRITICAL) {
    return { minGapMs: 2_000, maxPasses: 10, intervalMs: 12_000 }
  }
  if (buffer >= FARM_SYNC_BUFFER_HIGH) {
    return { minGapMs: 3_000, maxPasses: 8, intervalMs: 18_000 }
  }
  if (buffer >= FARM_SYNC_BUFFER_MODERATE) {
    return { minGapMs: 4_000, maxPasses: 6, intervalMs: 25_000 }
  }
  return { minGapMs: 5_000, maxPasses: 5, intervalMs: FARM_SYNC_INTERVAL_MS }
}

let accrualBuffer = 0
/** Or farmé en attente de sync serveur (inclus dans le gain session HUD). */
export const combatGoldAccrual = ref(0)

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let flushInFlight: Promise<boolean> | null = null
let lastFlushAt = 0
let farmSyncTimeoutHandle: ReturnType<typeof setTimeout> | null = null
let farmSyncLoopActive = false
let lastSyncProfileLevel = 0

function syncProfileLevel(buffer: number): number {
  if (buffer >= FARM_SYNC_BUFFER_CRITICAL) return 3
  if (buffer >= FARM_SYNC_BUFFER_HIGH) return 2
  if (buffer >= FARM_SYNC_BUFFER_MODERATE) return 1
  return 0
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isPageVisible(): boolean {
  return typeof document === 'undefined' || !document.hidden
}

function bufferStorageKey(): string | null {
  const auth = useAuthStore()
  const userId = auth.user?.id
  if (!userId) return null
  return `${COMBAT_GOLD_BUFFER_KEY}_${userId}`
}

function persistAccrualBuffer() {
  try {
    const key = bufferStorageKey()
    if (!key || typeof sessionStorage === 'undefined') return
    if (accrualBuffer <= 0) {
      sessionStorage.removeItem(key)
      return
    }
    sessionStorage.setItem(key, String(accrualBuffer))
  } catch {
    /* quota / private mode */
  }
}

/** Restaure l’or en attente après reload (évite la perte perçue). */
export function restoreCombatGoldBufferFromSession(): void {
  try {
    const key = bufferStorageKey()
    if (!key || typeof sessionStorage === 'undefined') return
    const raw = sessionStorage.getItem(key)
    if (!raw) return
    const restored = Math.max(0, Math.floor(Number(raw)))
    if (restored <= 0) return
    accrualBuffer = Math.max(accrualBuffer, restored)
    syncAccrualRef()
    const level = syncProfileLevel(accrualBuffer)
    if (level > lastSyncProfileLevel) lastSyncProfileLevel = level
  } catch {
    /* ignore */
  }
}

function syncAccrualRef() {
  combatGoldAccrual.value = accrualBuffer
  persistAccrualBuffer()
  try {
    usePlayerStore().goldPendingLocal = accrualBuffer
  } catch {
    /* store pas encore prêt */
  }
  const level = syncProfileLevel(accrualBuffer)
  if (level > lastSyncProfileLevel) {
    lastSyncProfileLevel = level
    rescheduleFarmSyncLoop()
  }
}

/** Aligne le buffer composable après dépense optimiste côté store. */
export function setCombatGoldAccrualBuffer(value: number) {
  accrualBuffer = Math.max(0, Math.floor(value))
  syncAccrualRef()
}

export function resetCombatGoldAccrual() {
  accrualBuffer = 0
  lastSyncProfileLevel = 0
  combatGoldAccrual.value = 0
  persistAccrualBuffer()
  try {
    usePlayerStore().goldPendingLocal = 0
  } catch {
    /* store pas encore prêt */
  }
  rescheduleFarmSyncLoop()
}

export function hasPendingCombatGold(): boolean {
  return accrualBuffer > 0
}

/** Farm combat : buffer local + crédit serveur périodique. */
export function accrueCombatGold(amount: number, opts?: { isShinyKill?: boolean }) {
  const n = Math.max(0, Math.floor(amount))
  if (n <= 0) return
  accrualBuffer += n
  syncAccrualRef()
  if (opts?.isShinyKill || n >= EXPRESS_GAIN_GOLD) {
    scheduleFlush(DEBOUNCE_EXPRESS_MS)
  }
}

export function applyWalletFromServer(gold: number, goldNonce?: number) {
  const player = usePlayerStore()
  player.applyWalletFromServer(gold, goldNonce)
}

function buildFarmSyncPayload(amount: number): Record<string, unknown> {
  const auth = useAuthStore()
  const player = usePlayerStore()
  const game = useGameStore()
  game.saveOfflineSnapshot()

  return {
    amount: Math.max(0, Math.floor(amount)),
    xp: player.xp,
    level: player.level,
    currentGeneration: player.currentGeneration,
    currentZone: player.currentZone,
    currentStage: player.currentStage,
    stageKills: player.stageKills ?? 0,
    teamDpsBonus: player.teamDpsBonus,
    badges: player.badges,
    totalKills: player.totalKills,
    combatGeneration: player.isFrontierReturnGuarded() ? null : player.combatGeneration,
    combatZone: player.isFrontierReturnGuarded() ? null : player.combatZone,
    adminVersion: player.adminVersion,
    snapshotDps: game.snapshotDps,
    snapshotZoneDps: game.snapshotZoneDps,
    snapshotGoldRate: game.snapshotGoldRate,
    snapshotEnemyMaxHp: game.snapshotEnemyMaxHp,
    snapshotGoldReward: game.snapshotGoldReward,
    sessionToken: auth.sessionToken,
    serverBootId: auth.serverBootId,
  }
}

/**
 * Checkpoint + crédit or (si buffer > 0) en une seule requête.
 */
export async function flushFarmSync(options?: {
  maxPasses?: number
  force?: boolean
  keepalive?: boolean
}): Promise<boolean> {
  if (!options?.keepalive && (!isPageVisible() || !isGameTabLeader())) return false

  const auth = useAuthStore()
  if (!auth.isAuthenticated || !auth.gameLoaded) return false

  const profile = resolveFarmSyncProfile(accrualBuffer)
  const now = Date.now()
  if (
    !options?.force
    && !options?.keepalive
    && now - lastFlushAt < profile.minGapMs
  ) {
    return true
  }

  if (flushInFlight) {
    return flushInFlight
  }

  const maxPasses = Math.max(1, options?.maxPasses ?? profile.maxPasses)

  flushInFlight = (async () => {
    try {
      await auth.resyncSessionTokenIfMissing()
      if (!auth.sessionToken) return false

      const api = useApi()
      let anyCredited = false
      const fetchOpts = options?.keepalive ? { keepalive: true } : undefined

      for (let pass = 0; pass < maxPasses; pass++) {
        const amount = accrualBuffer
        const payload = buildFarmSyncPayload(amount)
        let res: {
          gold: number
          goldNonce: number
          goldCredited: number
          killsDelta?: number
          killsCredited?: number
          goldPerKill?: number
          goldPendingEstimate?: number
          totalKills?: number
          totalKillsAdjusted?: boolean
          reload?: boolean
          code?: string
        }
        try {
          res = await api.post<typeof res>('/api/game/farm-sync', payload, fetchOpts)
        } catch (e: unknown) {
          const status = (e as { status?: number })?.status
          if (options?.force && status === 503 && pass < maxPasses - 1) {
            const retryAfterSec = Math.max(
              1,
              Math.floor(Number((e as { retryAfter?: number })?.retryAfter ?? 1)),
            )
            await sleep(retryAfterSec * 1000)
            continue
          }
          throw e
        }

        const reload = res as { reload?: boolean; code?: string }
        if (reload?.reload) {
          persistAccrualBuffer()
          if (reload.code === 'SERVER_UPDATED') {
            return false
          }
          const savedBuffer = accrualBuffer
          await auth.loadGameState()
          accrualBuffer = savedBuffer
          syncAccrualRef()
          void flushFarmSync({ force: true, maxPasses: 8 })
          return true
        }

        const credited = Math.max(0, Math.floor(Number(res?.goldCredited ?? 0)))
        if (res?.gold !== undefined) {
          applyWalletFromServer(res.gold, res.goldNonce)
        }
        if (typeof res?.totalKills === 'number' && Number.isFinite(res.totalKills)) {
          const player = usePlayerStore()
          const serverKills = Math.max(0, Math.floor(res.totalKills))
          // Monotone côté client : ne jamais redescendre sous le compteur local en session,
          // sauf si le serveur a explicitement ajusté (anti-abus).
          if (res.totalKillsAdjusted) {
            player.totalKills = serverKills
          } else {
            player.totalKills = Math.max(player.totalKills, serverKills)
          }
        }
        if (amount > 0) {
          // Estimation serveur (bornée par la capacité kills restante : purge les buffers
          // fantômes déjà crédités) + or accrué pendant la requête en vol (à préserver,
          // sinon les kills tombés durant l'await seraient écrasés et perdus à l'affichage).
          const serverPending = Math.max(
            0,
            Math.floor(Number(res?.goldPendingEstimate ?? amount - credited)),
          )
          const accruedDuringRequest = Math.max(0, accrualBuffer - amount)
          // Si le serveur n'a rien crédité et propose de tout purger, on garde le buffer :
          // purge fantôme trop agressive (race kill/anim, rate-limit) → saut HUD 3M→banque.
          if (credited <= 0 && serverPending <= 0 && amount > 0) {
            syncAccrualRef()
            break
          }
          accrualBuffer = serverPending + accruedDuringRequest
          syncAccrualRef()
          if (credited > 0) anyCredited = true
          if (serverPending > 0 && credited < amount) break
        } else {
          // Checkpoint seul : réaligner le pending HUD sur le buffer (ne pas le zéroter).
          syncAccrualRef()
          break
        }
      }

      lastFlushAt = Date.now()
      if (accrualBuffer <= FARM_SYNC_BUFFER_MODERATE) {
        lastSyncProfileLevel = syncProfileLevel(accrualBuffer)
      }
      rescheduleFarmSyncLoop()
      return anyCredited || accrualBuffer <= 0
    } catch (e: unknown) {
      console.warn('[farm-sync] flush failed:', e)
      return false
    } finally {
      flushInFlight = null
    }
  })()

  return flushInFlight
}

/** @deprecated Alias — utilise flushFarmSync. */
export const flushCombatGoldCredit = flushFarmSync

export function beaconFlushCombatGoldCredit(): void {
  if (!isPageVisible() || !isGameTabLeader()) return
  void flushFarmSync({ keepalive: true, force: true })
}

function scheduleFlush(delayMs: number) {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    void flushFarmSync()
  }, delayMs)
}

function clearFarmSyncTimeout() {
  if (farmSyncTimeoutHandle) {
    clearTimeout(farmSyncTimeoutHandle)
    farmSyncTimeoutHandle = null
  }
}

function scheduleFarmSyncTick() {
  clearFarmSyncTimeout()
  if (!farmSyncLoopActive) return
  const { intervalMs } = resolveFarmSyncProfile(accrualBuffer)
  farmSyncTimeoutHandle = setTimeout(() => {
    farmSyncTimeoutHandle = null
    void flushFarmSync().finally(() => scheduleFarmSyncTick())
  }, intervalMs)
}

function rescheduleFarmSyncLoop() {
  if (!farmSyncLoopActive) return
  scheduleFarmSyncTick()
}

export function stopFarmSyncInterval() {
  farmSyncLoopActive = false
  clearFarmSyncTimeout()
}

/** @deprecated Alias */
export const stopCombatGoldCreditInterval = stopFarmSyncInterval

/** Boucle périodique checkpoint + or (remplace credit-gold + checkpoint séparés). */
export function ensureFarmSyncLoop() {
  restoreCombatGoldBufferFromSession()
  if (farmSyncLoopActive) return
  farmSyncLoopActive = true
  scheduleFarmSyncTick()
  setTimeout(() => {
    void flushFarmSync()
  }, FARM_SYNC_FIRST_FLUSH_MS)
}

/** @deprecated Alias */
export const ensureCombatGoldCreditLoop = ensureFarmSyncLoop

export function teardownCombatGoldCredit() {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  stopFarmSyncInterval()
}

export async function reconcileWalletFromServer(): Promise<void> {
  await flushFarmSync({ force: true, maxPasses: 8 })
  const auth = useAuthStore()
  if (auth.isAuthenticated) {
    await auth.loadGameState()
  }
  if (accrualBuffer <= 0) {
    resetCombatGoldAccrual()
  }
}
